import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Bot, Loader2, Plus, Trash2 } from "lucide-react";
import { useBots, useDeleteBot } from "../hooks/useApi";
import { Skeleton } from "@/components/ui/skeleton";
import { useMinLoadingTime } from "@/hooks/useMinLoadingTime";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { SchedaAssistente } from "../components/assistenti/SchedaAssistente";
import { BrandCard, BrandStyles, VARIABILI_SEZIONE } from "../components/integrations/brand";
import { BOTTONE, GRIGIO, IntestazioneCasella, SCURO } from "../components/integrations/casella";
import { useTranslation } from "../hooks/useTranslation";

/** Un errore in pagina: titolo e messaggio. */
function Avviso({ titolo, testo }) {
  return (
    <div role="alert" className="flex items-start gap-3 rounded-[14px] border border-red-200 bg-red-50 p-4">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-sm font-medium text-red-800">{titolo}</p>
        {testo ? <p className="mt-1 text-sm text-red-700">{testo}</p> : null}
      </div>
    </div>
  );
}

/** Gli assistenti dell'organizzazione, a schede, con la creazione e l'eliminazione. */
const BotsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: bots, isLoading, error } = useBots();
  const showSkeleton = useMinLoadingTime(isLoading);
  const deleteBotMutation = useDeleteBot();
  const [botToDelete, setBotToDelete] = useState(null);

  const assistenti = Array.isArray(bots) ? bots : [];

  const handleDeleteBot = async () => {
    if (!botToDelete) return;
    try {
      await deleteBotMutation.mutateAsync(botToDelete._id);
      setBotToDelete(null);
    } catch (e) {
      console.error("Failed to delete bot:", e);
    }
  };

  const creaAssistente = (
    <button type="button" className={SCURO} onClick={() => navigate("/bots/new")}>
      <Plus className="h-3.5 w-3.5" aria-hidden="true" />
      {t("botsList.createAssistant")}
    </button>
  );

  return (
    <div className="relative isolate" style={VARIABILI_SEZIONE}>
      <BrandStyles />
      <IntestazioneCasella
        icona={Bot}
        titolo={t("botsList.titleAssistants")}
        dettagli={
          <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[#475569]">
            {!showSkeleton && !error ? (
              <span>{t("botsList.assistantsCount", { count: assistenti.length })}</span>
            ) : null}
            <span className="line-clamp-1 max-w-xl text-[#64748B]">{t("botsList.assistantsSubtitle")}</span>
          </span>
        }
        azioni={creaAssistente}
      />

      <div className="pu-fade-up mx-auto max-w-[1400px] space-y-4 pb-10">
        {deleteBotMutation.error ? (
          <Avviso titolo={t("botsList.deleteFailed")} testo={deleteBotMutation.error.message} />
        ) : null}

        {showSkeleton ? (
          <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-[214px] rounded-[14px]" />
            ))}
          </div>
        ) : null}

        {!showSkeleton && error ? <Avviso titolo={t("botsList.errorLoading")} testo={error.message} /> : null}

        {!showSkeleton && !error && assistenti.length > 0 ? (
          <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
            {assistenti.map((bot, i) => (
              <SchedaAssistente key={bot._id} bot={bot} indice={i} onElimina={setBotToDelete} />
            ))}
          </div>
        ) : null}

        {!showSkeleton && !error && assistenti.length === 0 ? (
          <BrandCard className="flex flex-col items-center px-6 py-12 text-center">
            <p className="text-base font-semibold text-[#020617]">{t("botsList.noAssistants")}</p>
            <p className="mt-1 max-w-md text-sm text-[#64748B]">{t("botsList.noAssistantsDesc")}</p>
            <div className="mt-5">{creaAssistente}</div>
          </BrandCard>
        ) : null}
      </div>

      <Dialog open={Boolean(botToDelete)} onOpenChange={(aperto) => !aperto && setBotToDelete(null)}>
        <DialogContent className="rounded-[14px] sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>{t("botsList.deleteTitle", { name: botToDelete?.name })}</DialogTitle>
            <DialogDescription>{t("botsList.deleteText")}</DialogDescription>
          </DialogHeader>
          <div className="rounded-[10px] border border-[#E2E8F0] bg-[#F8FAFC] p-4 text-sm text-[#475569]">
            <p>{t("botsList.deleteWillRemove")}</p>
            <ul className="mt-2 list-inside list-disc space-y-1">
              <li>{t("botsList.deleteItemAssistant")}</li>
              <li>{t("botsList.deleteItemAllSources")}</li>
              <li>{t("botsList.deleteItemWidget")}</li>
            </ul>
          </div>
          <DialogFooter className="gap-2 sm:space-x-0">
            <button type="button" className={GRIGIO} onClick={() => setBotToDelete(null)} disabled={deleteBotMutation.isPending}>
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className={`${BOTTONE} bg-[#DC2626] text-white hover:bg-[#B91C1C]`}
              onClick={handleDeleteBot}
              disabled={deleteBotMutation.isPending}
            >
              {deleteBotMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {deleteBotMutation.isPending ? t("botsList.deleting") : t("botsList.deleteCta")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BotsList;
